<script setup lang="ts">
import {
  EnumerationEvaluationOptionDto,
  EvaluationParameterDto,
  TrackItTaskWorkResultDto,
} from '@/api/generator';
import AnalysisResults from '@/components/measurement/AnalysisResults.vue';
import CommentInlineDialog from '@/components/comments/CommentInlineDialog.vue';
import EvaluationState from '@/components/evaluation/EvaluationState.vue';
import { computed } from 'vue';
import TrackItDataPointsTable from './TrackItDataPointsTable.vue';
import TrackItAnalysisValueCell from './TrackItAnalysisValueCell.vue';
import TrackItMeasurementInfoDetails from './TrackItMeasurementInfoDetails.vue';

const props = withDefaults(
  defineProps<{
    result: TrackItTaskWorkResultDto;
    evaluations?: EvaluationParameterDto[] | null;
    loading?: boolean;
    manualEntryEnabled?: boolean;
  }>(),
  {
    evaluations: undefined,
    loading: false,
    manualEntryEnabled: true,
  },
);

const emits = defineEmits<{
  manualValue: [
    evaluation: EvaluationParameterDto,
    value: number | EnumerationEvaluationOptionDto | null,
  ];
}>();

const evaluationResults = computed(() => props.result.evaluationResults ?? []);
</script>

<template>
  <div class="d-flex align-center w-100 mb-2">
    <EvaluationState :value="result.output || undefined" class="ml-auto" />

    <!-- This preview only ever shows a not-yet-applied candidate (or the current state of an
    already-applied one) — never a place to discuss it, so the comment list here is view-only.
    Placed last (rightmost), matching the comment icon's position in the applied job-occurrence
    panel. -->
    <CommentInlineDialog
      :entity="result"
      entityWithCommentType="WorkResult"
      readonly
      :showCountAlways="false"
    />
  </div>

  <v-row>
    <v-col>
      <AnalysisResults
        :value="evaluationResults"
        :evaluations="evaluations ?? undefined"
        :options="{
          enableComments: true,
          enableTrends: false,
          readonlyComments: true,
          commentCountAlways: false,
        }"
      >
        <template #value="{ item, deviation, disabled }">
          <TrackItAnalysisValueCell
            :evaluation="item.evaluationInfo"
            :deviation="deviation"
            :result="item.result"
            :hasValue="item.hasValue"
            :editable="
              manualEntryEnabled &&
              (result.unmappedEvaluationIds ?? []).includes(item.evaluationInfo.id)
            "
            :disabled="disabled"
            :submitting="loading"
            @input="(value) => emits('manualValue', item.evaluationInfo, value)"
          />
        </template>
      </AnalysisResults>
    </v-col>
  </v-row>

  <v-row v-if="result.trackItDataTypesWithoutEvaluation?.length">
    <v-col>
      <TrackItDataPointsTable
        title="Track-It Data Points without Evaluation"
        :dataPoints="result.trackItDataTypesWithoutEvaluation"
      />
    </v-col>
  </v-row>

  <v-row v-if="result.trackItMeasurement">
    <v-col>
      <TrackItMeasurementInfoDetails :measurement="result.trackItMeasurement" />
    </v-col>
  </v-row>
</template>
